import React, { useState, useRef } from 'react';
import CanvasDraw from 'react-canvas-draw';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2TextInput } from '@uhg-abyss/web/ui/TextInput';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useTinRegistration } from '@src/state/tinRegistration';
import { useUser } from '@src/state/user/hooks';

const SectionStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});
const ActionLinksGroup = styled('div', {
  display: 'flex',
  gap: '60px',
  margin: '16px 0 0 30px',
});
const SignatureStatement = styled('p', {
  fontSize: '16px',
  color: '$core.color.neutral.70',
  lineHeight: '1.5',
});
const SignatureActionLink = styled('span', {
  fontSize: '16px',
  fontWeight: 700,
  color: '$info1',
  cursor: 'pointer',
  '&:hover': { color: '$primary1' },
});
const RequiredAsterisk = styled('span', {
  color: '$error1',
  marginLeft: '1px',
});
const UploadingMessage = styled('div', {
  fontSize: '14px',
  fontWeight: 600,
  color: '$accent1',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});
const UploadSuccessMessage = styled('div', {
  fontSize: '14px',
  fontWeight: 600,
  color: '$success1',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});
const ErrorMessage = styled('div', {
  fontSize: '14px',
  fontWeight: 600,
  color: '$error1',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const DrawSignatureTab = ({ activeTab }) => {
  const form = useFormV2();
  const { uploadProof, setDocumentId } = useTinRegistration.actions();
  const { trackingNumber, documentIds } = useTinRegistration.state();
  const registration = form.getValues('searchRegistration');
  const { isCheckoutUser } = useUser.state();
  const drawCanvasRef = useRef(null);

  const [hasDrawing, setHasDrawing] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [disableSave, setDisableSave] = useState(false);

  const update = (patch) => {
    if ('hasDrawing' in patch) setHasDrawing(patch.hasDrawing);
    if ('uploadLoading' in patch) setUploadLoading(patch.uploadLoading);
    if ('uploadSuccess' in patch) setUploadSuccess(patch.uploadSuccess);
    if ('uploadError' in patch) setUploadError(patch.uploadError);
    if ('disableSave' in patch) setDisableSave(patch.disableSave);
  };

  const getBinaryFile = (img) => {
    const blobBin = atob(img.split(',')[1]);
    const array = [];
    for (let i = 0; i < blobBin.length; i++) array.push(blobBin.charCodeAt(i));
    return new Blob([new Uint8Array(array)], {
      type: 'image/png',
      encoding: 'utf-8',
    });
  };

  const handleSave = () => {
    update({ uploadSuccess: false, uploadError: false });
    if (!hasDrawing) {
      form.trigger('attestation.drawSignature');
      return;
    }
    const sourceCanvas = drawCanvasRef.current?.canvas?.drawing;
    if (!sourceCanvas || !sourceCanvas.width || !sourceCanvas.height) return;
    const destCanvas = document.createElement('canvas');
    destCanvas.width = sourceCanvas.width;
    destCanvas.height = sourceCanvas.height;
    const destCtx = destCanvas.getContext('2d');
    destCtx.drawImage(sourceCanvas, 0, 0);
    destCtx.globalCompositeOperation = 'destination-over';
    destCtx.fillStyle = 'white';
    destCtx.fillRect(0, 0, destCanvas.width, destCanvas.height);
    const drawBlob = getBinaryFile(destCanvas.toDataURL('image/png'));
    const drawFile = new File([drawBlob], `Signature-${trackingNumber}.png`, {
      type: 'image/png',
    });
    update({ uploadLoading: true });
    uploadProof(
      {
        billingTin: registration?.billingTin,
        requestId: registration?.requestId,
        fileType: 'US',
        trackingNumber,
        file: drawFile,
        test: isCheckoutUser,
      },
      (resp) => {
        if (!resp?.doc360Id) {
          update({ uploadLoading: false, uploadError: true });
          return;
        }
        setDocumentId({
          id: 'attestation.drawSignature',
          value: resp.doc360Id,
        });
        form.setValue('attestation.drawSignature', 'saved');
        form.clearErrors('attestation.drawSignature');
        update({
          disableSave: true,
          uploadLoading: false,
          uploadSuccess: true,
        });
      },
      () => update({ uploadLoading: false, uploadError: true })
    );
  };

  const handleClear = () => {
    update({
      hasDrawing: false,
      uploadSuccess: false,
      uploadError: false,
      disableSave: false,
    });
    form.setValue('attestation.drawSignature', '');
    form.clearErrors('attestation.drawSignature');
    setDocumentId({ id: 'attestation.drawSignature', value: undefined });
    if (drawCanvasRef.current) drawCanvasRef.current.clear();
  };

  return (
    <SectionStack>
      <label
        style={{ fontSize: '16px', fontWeight: 500, marginBottom: '-8px' }}
      >
        Digital signature <RequiredAsterisk>*</RequiredAsterisk>
      </label>

      <div
        onPointerUp={() => {
          if (documentIds?.['attestation.drawSignature']) {
            setDocumentId({
              id: 'attestation.drawSignature',
              value: undefined,
            });
            form.setValue('attestation.drawSignature', '');
            update({
              uploadSuccess: false,
              uploadError: false,
              disableSave: false,
            });
          }
        }}
      >
        <CanvasDraw
          style={{
            border: '1px solid black',
            borderRadius: '3px',
            cursor: 'crosshair',
            width: '100%',
          }}
          canvasWidth={1000}
          canvasHeight={100}
          hideInterface
          brushRadius={1}
          lazyRadius={0}
          hideGrid
          brushColor="black"
          onChange={() => update({ hasDrawing: true })}
          ref={(ref) => {
            drawCanvasRef.current = ref;
          }}
        />
      </div>

      {(!!form.formState?.errors?.attestation?.drawSignature ||
        (hasDrawing && !documentIds?.['attestation.drawSignature'])) && (
        <ErrorMessage>
          <IconSymbol
            icon="error"
            variant="outlined"
            color="$error1"
            size="20px"
          />
          <span>
            {hasDrawing
              ? 'Please save the signature before submitting.'
              : 'Digital signature is required.'}
          </span>
        </ErrorMessage>
      )}

      {uploadLoading && (
        <UploadingMessage>Saving signature, please wait...</UploadingMessage>
      )}
      {uploadSuccess && (
        <UploadSuccessMessage>
          <IconSymbol
            icon="check_circle"
            variant="outlined"
            color="$success1"
            size="20px"
          />
          <span>Signature has been saved successfully.</span>
        </UploadSuccessMessage>
      )}
      {uploadError && (
        <ErrorMessage>
          <IconSymbol
            icon="error"
            variant="outlined"
            color="$error1"
            size="20px"
          />
          <span>Failed to save signature, please try again.</span>
        </ErrorMessage>
      )}

      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          opacity: 0,
          pointerEvents: 'none',
        }}
      >
        <V2TextInput
          model="attestation.drawSignature"
          label="Digital signature"
          validators={{
            required: activeTab === 1 ? 'Digital signature is required.' : '',
          }}
        />
      </div>

      <SignatureStatement>
        I agree that the signature will be the electronic representation of my
        signature for all purposes when I use them on documents, including
        legally binding contracts - just the same as a pen-and-paper signature
        or initial.
      </SignatureStatement>
      <ActionLinksGroup>
        <SignatureActionLink
          onClick={handleSave}
          style={{
            opacity: disableSave ? 0.4 : 1,
            pointerEvents: disableSave ? 'none' : 'auto',
          }}
        >
          Save signature
        </SignatureActionLink>
        <SignatureActionLink onClick={handleClear}>
          Clear signature
        </SignatureActionLink>
      </ActionLinksGroup>
    </SectionStack>
  );
};
